import { ChevronDown } from 'lucide-react';
import { type ReactElement, type ReactNode, useId, useState } from 'react';
import { cn } from '@/shared/ui';

interface FilterSectionProps {
  /** 섹션 제목 — 펼침 버튼의 이름이 된다. */
  readonly title: string;
  readonly defaultOpen?: boolean;
  readonly children: ReactNode;
}

/**
 * 접이식 필터 섹션 — 제목 행을 누르면 내용을 펼치고 접는다.
 * 접혀도 내용은 DOM 에 남긴다(hidden) — 입력 중이던 값을 잃지 않게.
 */
export const FilterSection = ({
  title,
  defaultOpen = false,
  children,
}: FilterSectionProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    // 마지막 섹션 아래에는 선을 남기지 않는다.
    <section className="border-b border-foreground/10 last:border-b-0">
      <h3>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={() => setIsOpen((value) => !value)}
          className="flex h-12 w-full cursor-pointer items-center justify-between text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {title}
          <ChevronDown
            aria-hidden
            strokeWidth={1.5}
            className={cn(
              'size-4 text-muted-foreground transition-transform',
              isOpen && 'rotate-180',
            )}
          />
        </button>
      </h3>
      <div id={contentId} hidden={!isOpen} className="pb-4">
        {children}
      </div>
    </section>
  );
};
